/**
 * Holds the Show By grain a viewer has chosen, falling back to the report's own created grain.
 *
 * Nothing is sent until someone actually picks a grain: that is what lets the report serve the grain
 * it was created at without the client having to know what that is. The response then reports which
 * grain it rendered, and the control adopts it — so the picker shows the report's real default rather
 * than a hardcoded "Monthly" that would be wrong on every annual deck.
 */

import { useCallback, useEffect, useState } from 'react';
import { asShowBy, type ShowBy } from 'src/components/star-grid/utils/showBy';

export interface UseShowByResult {
  /** What the picker displays: the viewer's choice, else the grain the report reported. */
  readonly value: ShowBy | undefined;
  readonly setValue: (next: ShowBy) => void;
  /** What the query sends. `undefined` leaves the report on its own grain. */
  readonly requested: ShowBy | undefined;
}

export function useShowBy(reportedShowBy: string | null | undefined): UseShowByResult {
  const [requested, setRequested] = useState<ShowBy | undefined>(undefined);
  const [reported, setReported] = useState<ShowBy | undefined>(undefined);

  useEffect(() => {
    const found = asShowBy(reportedShowBy);
    // Kept once seen: a later response that omits the field must not blank the picker.
    if (found) setReported(found);
  }, [reportedShowBy]);

  return {
    value: requested ?? reported,
    setValue: useCallback((next: ShowBy) => setRequested(next), []),
    requested
  };
}
